import { parseSheetDate, formatDateDDMMYYYY } from './dates';
import { rentRowAmounts, baseRateFromRows, SHORT_RENT_NOTE } from './rentRows';
import { dubaiToday, isOpenStatus } from '../../../services/contractRules';

/**
 * Utility to synchronize open contracts by detecting and generating missing monthly rows.
 * This mirrors the logic of MonthlyCalculator.js but for bulk processing.
 *
 * Rent is priced by the shared rentRows helpers (the same ones MonthlyCalculator
 * uses), so a given period always costs the same regardless of which path
 * generated the row: 28+ days = full base rate, shorter = prorated on a 30-day
 * month. Previously this file always billed the full base rate.
 */
export const syncMissingRowsForOpenContracts = (allContracts, today = dubaiToday()) => {
    const parseNumber = (val) => {
        if (typeof val === 'number') return val;
        if (!val) return 0;
        return parseFloat(val.toString().replace(/,/g, '')) || 0;
    };

    const limitDate = new Date(today);
    limitDate.setUTCHours(0, 0, 0, 0);

    const updates = [];

    allContracts.forEach(doc => {
        if (!isOpenStatus(doc)) return;   // a blank status is not open

        const initialRows = Array.isArray(doc.monthly_calculations) ? [...doc.monthly_calculations] : [];
        const pickupDateObj = parseSheetDate(doc.pickup_date || doc['Pick-up Date'] || doc.pickupDate);
        
        if (!pickupDateObj) return;

        // Determine baseRate for pre-filling newly added rows: the contract's monthly
        // amount, total_rent first (owner, 26 Sep 2026 - it used to be read only when the
        // contract had no rows, so a first row wins over a newer rate). Without one: the
        // rows' rate (baseRateFromRows: the first full-length row, else the first row -
        // the first month is always the full monthly amount), else the first row's raw
        // totalRent when it has no parseable period.
        const baseRate = parseNumber(doc.total_rent) || parseNumber(doc.rent) || parseNumber(doc['Total Rent'])
            || (initialRows.length > 0 ? (baseRateFromRows(initialRows)
                || (initialRows[0].rateNote === SHORT_RENT_NOTE ? 0 : parseNumber(initialRows[0].totalRent)) || 0) : 0);

        if (baseRate === 0) return;

        let currentStart;
        if (initialRows.length > 0) {
            const lastRow = initialRows[initialRows.length - 1];
            currentStart = parseSheetDate(lastRow.end);
        } else {
            currentStart = new Date(pickupDateObj);
        }

        if (!currentStart) return;
        currentStart.setUTCHours(0, 0, 0, 0);

        let modified = false;

        while (true) {
            // Stop if we have at least one row and we are past the limitDate
            if (currentStart > limitDate && initialRows.length > 0) break;

            const end = new Date(currentStart);
            end.setUTCDate(end.getUTCDate() + 30);
            
            const finalEndDate = end; // No clamping for sync usually, just full months

            // Same pricing rule as the manual editor: full rate for a 30+ day
            // period (or the first month), prorated on a 30-day month for anything shorter.
            const { totalRent: newTotalRent, rent: newRent, vat: newVat } = rentRowAmounts({
                start: currentStart,
                end: finalEndDate,
                baseRate,
                first: initialRows.length === 0
            });

            initialRows.push({
                start: formatDateDDMMYYYY(currentStart),
                end: formatDateDDMMYYYY(finalEndDate),
                totalRent: newTotalRent,
                rent: newRent,
                vat: newVat,
                salik: 0, parking: 0, fine: 0, other: 0, paid: 0, date: '-', invNo: '',
                currentKM: 0
            });
            
            modified = true;
            
            if (currentStart > limitDate) break;
            currentStart = new Date(end);
            currentStart.setUTCHours(0, 0, 0, 0);
        }

        if (modified) {
            // Only the month rows: the whole row (customer, status, comments ... as read) used to go
            // back too. lastUpdated = the version the rows were built from; the save is refused when
            // the contract changed since (a Fill from Ejar meanwhile) - CON-03.
            updates.push({
                id: doc.id || doc.contract_no || doc['Contract No.'],
                lastUpdated: doc._last_updated ?? '',
                data: {
                    monthly_calculations: initialRows,
                    _syncUpdate: true // Flag for tracking
                }
            });
        }
    });

    return updates;
};
